import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ArrowLeft, Send, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { conciergeApi, errMsg } from "@/components/concierge/api";
import { ArticlePicker } from "@/components/concierge/ArticlePicker";
import { toggleDepositStep } from "@/components/concierge/depositSteps";
import { DepositHistoryList } from "@/components/concierge/DepositHistoryList";

export const DepositForm = ({ person, onBack }) => {
  const [articles, setArticles] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [tracking, setTracking] = useState("");
  const [deposits, setDeposits] = useState([]);
  const [credit, setCredit] = useState(person.credit || 0);
  const [sending, setSending] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const loadDeposits = useCallback(async () => {
    const res = await conciergeApi.get("/deposits", { params: { person_id: person.id } });
    setDeposits(res.data);
  }, [person.id]);

  useEffect(() => {
    conciergeApi.get("/articles").then((r) => setArticles(r.data));
    loadDeposits();
  }, [loadDeposits]);

  const toggleReturned = async (d, field) => {
    try {
      await toggleDepositStep(d, field);
      loadDeposits();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const subtotal = articles.reduce((s, a) => s + a.price * (quantities[a.id] || 0), 0);
  const creditUsed = person.type === "staff" ? Math.min(credit, subtotal) : 0;

  const submit = async () => {
    const items = Object.entries(quantities).filter(([, q]) => q > 0).map(([article_id, quantity]) => ({ article_id, quantity }));
    if (!items.length) { toast.error("Ajoutez au moins un article"); return; }
    setSending(true);
    try {
      const res = await conciergeApi.post("/deposits", { person_type: person.type, person_id: person.id, tracking_number: tracking, items });
      toast.success(res.data.email_sent ? `Bon ${res.data.number} envoyé par email` : `Bon ${res.data.number} enregistré (email non envoyé)`);
      setQuantities({}); setTracking(""); setShowForm(false);
      setCredit((c) => Math.max(0, c - res.data.credit_used));
      loadDeposits();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="concierge-deposit-form">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="deposit-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
        <div>
          <h3 className="text-lg font-bold">{person.first_name} {person.last_name}</h3>
          <p className="text-xs text-slate-500">{person.email}{person.company_name ? ` — ${person.company_name}` : ""}</p>
        </div>
        {person.type === "staff" && <span className="ml-auto bg-emerald-100 text-emerald-700 font-bold px-3 py-1 rounded-full text-sm" data-testid="deposit-credit">Avoir : {credit.toFixed(2)} €</span>}
      </div>
      {!showForm && (
        <Button className="w-full h-12 bg-pink-600 hover:bg-pink-700" onClick={() => setShowForm(true)} data-testid="deposit-new-btn">
          <Plus className="w-4 h-4 mr-2" />Nouveau bon de dépôt
        </Button>
      )}
      {showForm && (
        <div className="space-y-5 border-2 border-pink-200 rounded-2xl p-4" data-testid="deposit-new-form">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Nouveau bon de dépôt</h4>
            <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setQuantities({}); setTracking(""); }} data-testid="deposit-cancel-btn">Annuler</Button>
          </div>
      <div>
        <Input placeholder="N° de marquage (obligatoire)" value={tracking} onChange={(e) => setTracking(e.target.value)} required
          className={tracking.trim() ? "" : "border-red-300"} data-testid="deposit-tracking-input" />
        {!tracking.trim() && <p className="text-xs text-red-500 mt-1" data-testid="deposit-tracking-required">Le n° de marquage est obligatoire pour valider le bon.</p>}
      </div>
      <ArticlePicker articles={articles} quantities={quantities} setQty={(id, q) => setQuantities({ ...quantities, [id]: q })} />
      <div className="bg-slate-50 rounded-xl p-4 space-y-1 text-sm" data-testid="deposit-summary">
        {articles.filter((a) => quantities[a.id] > 0).map((a) => (
          <p key={a.id} className="flex justify-between text-slate-700" data-testid={`deposit-summary-line-${a.id}`}>
            <span>{quantities[a.id]} × {a.name} <span className="text-slate-400">({a.price.toFixed(2)} €)</span></span>
            <span>{(a.price * quantities[a.id]).toFixed(2)} €</span>
          </p>
        ))}
        {subtotal > 0 && <div className="border-t my-2" />}
        <p className="flex justify-between"><span>Sous-total</span><span>{subtotal.toFixed(2)} €</span></p>
        {creditUsed > 0 && <p className="flex justify-between text-emerald-700"><span>Avoir utilisé</span><span>-{creditUsed.toFixed(2)} €</span></p>}
        <p className="flex justify-between font-bold text-base"><span>Total</span><span data-testid="deposit-total">{(subtotal - creditUsed).toFixed(2)} €</span></p>
      </div>
      <Button className="w-full h-12 bg-pink-600 hover:bg-pink-700" onClick={submit} disabled={sending || subtotal === 0 || !tracking.trim()} data-testid="deposit-submit-btn">
        <Send className="w-4 h-4 mr-2" />{sending ? "Envoi..." : "Valider et envoyer le bon par email"}
      </Button>
        </div>
      )}
      <div>
        <h4 className="font-semibold mb-2">Historique des bons ({deposits.length})</h4>
        <DepositHistoryList deposits={deposits} onToggle={toggleReturned} />
      </div>
    </div>
  );
};
